import { ShieldAlert, ShieldCheck, Wallet } from 'lucide-react'
import { useAccount, useConnect, useDisconnect } from 'wagmi'
import { signInFailureReason } from '@/hooks/useAuth'
import { useReadOnlyState } from '@/hooks/useOps'
import { shortAddress } from '@/lib/format'
import { useSession } from '@/lib/session'
import { CONNECTOR_META, walletConnectEnabled } from '@/lib/wagmi'
import { Button } from '@/ui/Button'
import { Housing } from '@/ui/Card'
import { Badge, Callout } from '@/ui/primitives'

/**
 * The door.
 *
 * Three states, and they are genuinely different problems for the person looking at
 * them — which is why they are not collapsed into one "please sign in" screen:
 *
 *   no wallet connected   → pick a connector
 *   connected, no session → sign the message (or read why the last attempt failed)
 *   session, not an admin → this wallet proved itself and was still turned away
 *
 * The last one is the one worth separating. Telling someone to "sign in" when they
 * already have sends them round the same loop forever; the fix is to switch accounts.
 */
export function SignIn() {
  const { address, isConnected } = useAccount()
  const { connectors, connect, isPending: connecting, error: connectError } = useConnect()

  /*
   * Hide the generic injected shim once a wallet has announced itself over EIP-6963.
   *
   * With MetaMask installed, wagmi offers both a discovered "MetaMask" connector and
   * the catch-all "Injected" one, and they open the same wallet. Two rows that do the
   * same thing is a choice the operator has to think about and cannot get right. The
   * shim stays when nothing was discovered — that is the case it exists for.
   */
  const hasDiscoveredInjected = connectors.some(
    (c) => c.type === 'injected' && c.name !== 'Injected',
  )
  const visibleConnectors = hasDiscoveredInjected
    ? connectors.filter((c) => !(c.type === 'injected' && c.name === 'Injected'))
    : connectors
  const { disconnect } = useDisconnect()
  const { signIn, signingIn, signInError, isSignedInNotAdmin, signOut } = useSession()

  // Public endpoint, so it resolves before sign-in — the operator learns whether the
  // protocol is halted before they have even authenticated.
  const readOnly = useReadOnlyState()

  return (
    <div className="grid min-h-dvh place-items-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center gap-3">
          <img src="/brand/mark.svg" alt="" className="size-9" />
          <div>
            <p className="display text-lg leading-tight">CrystalDEX</p>
            <p className="label">Operations console</p>
          </div>
        </div>

        <Housing className="overflow-hidden">
          <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
            <span className="label">Protocol status</span>
            {readOnly.isLoading ? (
              <Badge tone="neutral">Checking</Badge>
            ) : readOnly.isError ? (
              <Badge tone="danger">API unreachable</Badge>
            ) : readOnly.data ? (
              <Badge tone="danger">Read-only</Badge>
            ) : (
              <Badge tone="positive">Live</Badge>
            )}
          </div>

          <div className="space-y-5 p-5">
            {isSignedInNotAdmin ? (
              <NotAuthorized address={address} onSignOut={() => void signOut()} />
            ) : !isConnected ? (
              <>
                <div>
                  <p className="display text-base">Connect an operator wallet</p>
                  <p className="mt-1.5 text-sm text-muted">
                    Access is granted by wallet address. Only addresses listed in the API's{' '}
                    <code className="font-mono text-xs text-ink">ADMIN_ADDRESSES</code> can
                    read this console.
                  </p>
                </div>

                <div className="space-y-2">
                  {visibleConnectors.map((connector) => {
                    /*
                     * A wallet the browser ANNOUNCED names itself; only the generic
                     * shim needs a label from us. Keying off `connector.type` alone
                     * rendered every injected wallet as an identical "Browser wallet"
                     * row, with no way to tell MetaMask from Rabby — and the hint
                     * listing four wallet names is only true of the shim, so a named
                     * connector drops it.
                     */
                    const meta = CONNECTOR_META[connector.type]
                    const named = connector.type === 'injected' && connector.name !== 'Injected'
                    const label = named ? connector.name : (meta?.label ?? connector.name)
                    const hint = named ? undefined : meta?.hint

                    return (
                      <button
                        key={connector.uid}
                        onClick={() => connect({ connector })}
                        disabled={connecting}
                        className="surface-glass-2 flex w-full items-center gap-3 rounded-[10px] px-4 py-3 text-left ring-1 ring-line transition-colors hover:ring-accent-edge disabled:opacity-50"
                      >
                        {connector.icon ? (
                          // The wallet's own mark, when it announced one.
                          <img src={connector.icon} alt="" className="size-4 shrink-0 rounded-sm" />
                        ) : (
                          <Wallet className="size-4 shrink-0 text-accent" aria-hidden />
                        )}
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-ink">{label}</span>
                          {hint && <span className="block text-xs text-faint">{hint}</span>}
                        </span>
                      </button>
                    )
                  })}
                </div>

                {!walletConnectEnabled && (
                  <p className="text-xs text-faint">
                    WalletConnect is unavailable — no{' '}
                    <code className="font-mono">VITE_WALLETCONNECT_PROJECT_ID</code> is set.
                    Browser wallets still work.
                  </p>
                )}

                {connectError && (
                  <Callout tone="danger" title="Could not connect">
                    {connectError.message}
                  </Callout>
                )}
              </>
            ) : (
              <>
                <div>
                  <p className="display text-base">Prove the wallet</p>
                  <p className="mt-1.5 text-sm text-muted">
                    Connecting only tells the browser an address — it proves nothing about
                    who controls it. Sign the message to open a session.
                  </p>
                </div>

                <div className="surface-glass-2 flex items-center justify-between gap-3 rounded-[10px] px-4 py-3 ring-1 ring-line">
                  <span className="label">Wallet</span>
                  <span className="tnum text-sm">{address ? shortAddress(address) : '—'}</span>
                </div>

                <div className="flex gap-2">
                  <Button
                    fullWidth
                    loading={signingIn}
                    onClick={() => void signIn().catch(() => {})}
                    iconLeft={<ShieldCheck className="size-4" aria-hidden />}
                  >
                    Sign in
                  </Button>
                  <Button variant="secondary" onClick={() => disconnect()}>
                    Disconnect
                  </Button>
                </div>

                {signInError && (
                  <Callout tone="danger" title="Sign-in failed">
                    {signInFailureReason(signInError as Error)}
                  </Callout>
                )}
              </>
            )}
          </div>
        </Housing>

        <p className="mt-6 text-center text-xs text-faint">
          Every action taken here is written to an append-only audit table with the acting
          address.
        </p>
      </div>
    </div>
  )
}

function NotAuthorized({ address, onSignOut }: { address?: string; onSignOut: () => void }) {
  return (
    <>
      <div className="flex items-start gap-3">
        <ShieldAlert className="mt-0.5 size-5 shrink-0 text-fault" aria-hidden />
        <div>
          <p className="display text-base">Not an operator</p>
          <p className="mt-1.5 text-sm text-muted">
            The signature verified, so this wallet is genuinely yours — it is simply not on
            the admin list. Switch to a listed account in your wallet, or have this address
            added to <code className="font-mono text-xs text-ink">ADMIN_ADDRESSES</code> and
            restart the API.
          </p>
        </div>
      </div>

      <div className="surface-glass-2 flex items-center justify-between gap-3 rounded-[10px] px-4 py-3 ring-1 ring-line">
        <span className="label">Signed in as</span>
        <span className="tnum text-sm">{address ? shortAddress(address) : '—'}</span>
      </div>

      <Button variant="secondary" fullWidth onClick={onSignOut}>
        Sign out
      </Button>
    </>
  )
}
